/**
 * Apply CPP modal — opened from Campaigns or Ad Groups BulkActions
 * → "Apply CPP".
 *
 * Design source: oneads-remix/project/cpp-popup.html (Claude Design
 * handoff). 1080×820px shell, 280px sidebar with search input above
 * a scrollable name list, right pane with selected-CPP header bar
 * over a 3-screenshot preview row, accent-50 footer with secondary
 * "Open in new tab" + primary "Apply".
 *
 * Logic unchanged from the previous iteration:
 *   - List of CPPs is provided by the parent (already streamed from
 *     /api/asa/custom-product-pages/stream).
 *   - onRefresh + isRefreshing surface from the streaming hook;
 *     refresh icon spins while in flight.
 *   - Apply triggers the parent's mutation; on `ok: true` the modal
 *     closes (parent's toast surfaces success), on `ok: false` we
 *     stay open and surface the error inline.
 *   - Search is client-side, substring match against `name`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import type { ReactNode } from "react";

export type CppOption = {
  id: string;
  name: string;
  /** Pre-resolved screenshot URLs (typically 1–3). When empty, the
   *  preview pane renders numbered placeholders per the design. */
  screenshots: string[];
  /** Public marketing-page URL (apps.apple.com/.../?ppid=…) for
   *  "Open in new tab". */
  externalUrl?: string;
};

type ApplyResult = { ok: true } | { ok: false; error: string };

const Svg = ({
  size = 16,
  children,
  strokeWidth = 1.75,
}: {
  size?: number;
  children: ReactNode;
  strokeWidth?: number;
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const ICONS = {
  search: (
    <Svg size={15}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Svg>
  ),
  refresh: (
    <Svg>
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </Svg>
  ),
  x: (
    <Svg>
      <path d="M6 6l12 12" />
      <path d="M6 18 18 6" />
    </Svg>
  ),
};

export function ApplyCppModal({
  open,
  onClose,
  selectedCount,
  entityKind = "campaign",
  cppOptions,
  onApply,
  onRefresh,
  isRefreshing = false,
}: {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  /** Drives the title copy ("Selected Campaigns" vs "Selected Ad Groups"). */
  entityKind?: "campaign" | "ad_group";
  cppOptions: CppOption[];
  onApply: (cppId: string) => Promise<ApplyResult>;
  /** Force-refresh from apps.apple.com (parent's stream hook). */
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // CPPs that the user has Apply'd during the current modal session.
  // Used to:
  //   1. disable the Apply button when the picked row is in this set
  //      (prevents accidental double-apply of the same CPP).
  //   2. re-enable Apply the moment the user picks a different row.
  // Resets on modal close/reopen.
  const [applied, setApplied] = useState<Set<string>>(new Set());

  // Queue + worker for sequential Apply runs. The user can click
  // Apply on multiple CPPs in succession — we enqueue them and run
  // one at a time so ASA doesn't see a thundering herd and so the
  // toast sequence reads naturally (one per Apply, in order).
  // queueRef + processingRef are refs so the worker survives
  // re-renders without being recreated.
  const queueRef = useRef<string[]>([]);
  const processingRef = useRef(false);
  const onApplyRef = useRef(onApply);
  // Keep the latest onApply in the worker's closure so picking a
  // different CPP mid-queue still hits the up-to-date prop.
  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  // Reset transient state on open. We deliberately do NOT depend on
  // `cppOptions` here — the CampaignsPage rebuilds that array every
  // render, so each parent re-render (e.g. after Apply triggers
  // mutation state changes) would otherwise reset the user's chosen
  // CPP back to the first item.
  useEffect(() => {
    if (open) {
      setQuery("");
      setPickedId(null);
      setApplied(new Set());
      // Don't clear queueRef on re-open — in-flight applies from a
      // previous session keep running. Empty array is the default
      // for a fresh session.
      queueRef.current = [];
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cppOptions;
    return cppOptions.filter((c) => c.name.toLowerCase().includes(q));
  }, [cppOptions, query]);

  // Auto-pick the first visible row when the current pick is missing
  // from the filtered set. Covers (a) initial population (pickedId is
  // null after open), (b) the user typing a query that excludes the
  // current pick. Crucially, when the user picks an item and the
  // streaming hook later updates cppOptions in place, the picked id
  // stays valid → no reset.
  useEffect(() => {
    if (!open) return;
    if (filtered.length === 0) return;
    if (!filtered.find((c) => c.id === pickedId)) {
      setPickedId(filtered[0].id);
    }
  }, [open, filtered, pickedId]);

  useLayer(open, onClose);

  if (!open) return null;

  const picked = cppOptions.find((c) => c.id === pickedId) ?? null;

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const next = queueRef.current.shift();
        if (!next) continue;
        try {
          await onApplyRef.current(next);
        } catch {
          // parent's mutation hook already surfaces error toasts.
          // We just continue the queue.
        }
      }
    } finally {
      processingRef.current = false;
    }
  };

  const handleApply = () => {
    if (!picked) return;
    if (applied.has(picked.id)) return;
    // Mark this CPP as "applied during this session" so the button
    // disables for it. Picking a different CPP re-enables Apply.
    setApplied((s) => {
      const next = new Set(s);
      next.add(picked.id);
      return next;
    });
    // Enqueue + kick the worker. Multiple clicks (on different
    // CPPs) accumulate; they run one after another, not in
    // parallel — keeps ASA pushes serialised and toast order
    // predictable. Modal stays open; user closes via X when ready.
    queueRef.current.push(picked.id);
    void processQueue();
  };

  const handleOpenInNewTab = () => {
    if (picked?.externalUrl) {
      window.open(picked.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  const titleSuffix = (() => {
    const kind = entityKind === "ad_group" ? "Ad Group" : "Campaign";
    const plural = selectedCount === 1 ? "" : "s";
    return `Selected ${kind}${plural}`;
  })();
  const subtitle = (() => {
    const noun = entityKind === "ad_group" ? "ad group" : "campaign";
    return `${selectedCount} ${noun}${selectedCount === 1 ? "" : "s"} selected`;
  })();

  return (
    <div
      className="cpp-overlay"
      onClick={(e) => {
        // Click outside the modal box closes — always available now,
        // queue keeps running in the background.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cpp-modal" role="dialog" aria-modal="true">
        <header className="cpp-head">
          <div className="cpp-head-titles">
            <h2>Apply Custom Product Page to {titleSuffix}</h2>
            <p>{subtitle}</p>
          </div>
          <div className="cpp-head-actions">
            {onRefresh && (
              <button
                type="button"
                className={"cpp-iconbtn " + (isRefreshing ? "spinning" : "")}
                onClick={() => !isRefreshing && onRefresh()}
                disabled={isRefreshing}
                aria-label="Refresh CPP list"
                title={
                  isRefreshing
                    ? "Refreshing CPPs from App Store…"
                    : "Refresh CPP list (re-scrape apps.apple.com)"
                }
              >
                {ICONS.refresh}
              </button>
            )}
            <button
              type="button"
              className="cpp-iconbtn"
              onClick={onClose}
              aria-label="Close"
            >
              {ICONS.x}
            </button>
          </div>
        </header>

        <div className="cpp-body">
          <aside className="cpp-side">
            <div className="cpp-search">
              <span className="cpp-search-ico">{ICONS.search}</span>
              <input
                type="search"
                placeholder="Search Custom Product Pages…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="cpp-list">
              {cppOptions.length === 0 && (
                <div className="cpp-list-empty">
                  No Custom Product Pages found.
                  <br />
                  Pages created in App Store Connect will appear here once
                  the ASA integration syncs them.
                </div>
              )}
              {cppOptions.length > 0 && filtered.length === 0 && (
                <div className="cpp-list-empty">No matches</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={
                    "cpp-item " + (c.id === pickedId ? "active" : "")
                  }
                  onClick={() => setPickedId(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </aside>

          <section className="cpp-preview">
            <div className="cpp-preview-head">{picked?.name ?? "—"}</div>
            <div className="cpp-preview-body">
              {(() => {
                // Render only as many slots as there are media (cap 3)
                // — fixed 3 slots painted black placeholders for CPPs
                // that genuinely have 1-2 assets. When there are no
                // media at all, fall back to a single numbered
                // placeholder so the area isn't collapsed.
                const shots = (picked?.screenshots ?? []).slice(0, 3);
                const slots: (string | null)[] =
                  shots.length > 0 ? shots : [null];
                return slots.map((url, i) => {
                  // Video poster URLs keep the App-Store `PurpleVideo`
                  // bucket marker (the screenshot bucket is
                  // `PurpleSource`). Show a ▶ badge + make the slot
                  // open the App Store page where the clip plays.
                  const isVideo = !!url && url.includes("/PurpleVideo");
                  return (
                    <div key={i} className="cpp-shot-wrap">
                      <div
                        className={
                          "cpp-shot" + (isVideo ? " cpp-shot-video" : "")
                        }
                        onClick={
                          isVideo && picked?.externalUrl
                            ? handleOpenInNewTab
                            : undefined
                        }
                        title={isVideo ? "App Preview video — open to play" : undefined}
                      >
                        {url ? (
                          <>
                            <img
                              src={url}
                              alt={
                                isVideo
                                  ? "App Preview video"
                                  : `Screenshot ${i + 1}`
                              }
                              className="cpp-shot-img"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                            {isVideo && (
                              <span
                                className="cpp-shot-play"
                                aria-label="App Preview video"
                              >
                                <svg viewBox="0 0 24 24" width="22" height="22">
                                  <path d="M8 5v14l11-7z" fill="currentColor" />
                                </svg>
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="cpp-shot-num">{i + 1}</div>
                            <div className="cpp-shot-lbl">
                              Screenshot {i + 1}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </section>
        </div>

        <footer className="cpp-foot">
          <button
            type="button"
            className="cpp-btn-secondary"
            onClick={handleOpenInNewTab}
            disabled={!picked?.externalUrl}
          >
            Open in new tab
          </button>
          <button
            type="button"
            className="cpp-apply"
            onClick={handleApply}
            disabled={!picked || applied.has(picked.id)}
            title={
              picked && applied.has(picked.id)
                ? "Already applied in this session — pick a different CPP to apply again"
                : undefined
            }
          >
            {picked && applied.has(picked.id) ? "Applied" : "Apply"}
          </button>
        </footer>
      </div>
    </div>
  );
}
